import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { X, Calculator, GripVertical, Home, Percent, Landmark, ShieldCheck, Building2, TrendingDown } from "lucide-react";
import { useLang } from "../context/LanguageContext";

// June 2026 indicative base rates by finance type (editable by user).
const LOAN_TYPES = {
  conventional: { rate: 6.85, down: 20, key: "conventional" },
  fha: { rate: 6.55, down: 3.5, key: "fha" },
  va: { rate: 6.45, down: 0, key: "va" },
  jumbo: { rate: 7.05, down: 20, key: "jumbo" },
  private: { rate: 8.75, down: 25, key: "private" },
};
// Credit tier -> rate adjustment (added to base rate).
const CREDIT_TIERS = [
  { id: "excellent", adj: 0.0, min: 760 },
  { id: "good", adj: 0.15, min: 700 },
  { id: "fair", adj: 0.4, min: 660 },
  { id: "belowAvg", adj: 0.8, min: 620 },
  { id: "poor", adj: 1.35, min: 0 },
];
// Rough county property-tax estimates for common FL ZIP prefixes (annual %).
const FL_TAX_BY_ZIP3 = {
  "331": { county: "Miami-Dade", tax: 1.02 },
  "330": { county: "Miami-Dade", tax: 1.02 },
  "333": { county: "Broward", tax: 1.11 },
  "334": { county: "Palm Beach", tax: 1.06 },
  "328": { county: "Orange (Orlando)", tax: 0.98 },
  "327": { county: "Volusia", tax: 0.95 },
  "336": { county: "Hillsborough (Tampa)", tax: 1.09 },
  "337": { county: "Pinellas", tax: 0.9 },
  "339": { county: "Lee (Fort Myers)", tax: 0.95 },
  "341": { county: "Collier (Naples)", tax: 0.71 },
  "322": { county: "Duval (Jacksonville)", tax: 0.94 },
};

const STR = {
  en: {
    title: "Mortgage Calculator", subtitle: "Estimate your monthly payment",
    homePrice: "Home price", downPayment: "Down payment", loanType: "Financing option",
    creditScore: "Credit score", term: "Loan term", rate: "Interest rate (APR)",
    zip: "Property ZIP", propertyTax: "Property tax", homeInsurance: "Home insurance",
    hoa: "HOA dues", pmi: "PMI / MIP", years: "yrs", perYear: "/yr", perMo: "/mo",
    monthlyPayment: "Estimated monthly payment", principalInterest: "Principal & interest",
    taxes: "Property taxes", insurance: "Insurance", hoaShort: "HOA",
    loanAmount: "Loan amount", totalInterest: "Total interest", totalCost: "Total of payments",
    downNote: "of price", advanced: "Taxes, insurance & fees", county: "County",
    types: { conventional: "Conventional / Bank", fha: "FHA", va: "VA", jumbo: "Jumbo", private: "Private Lender" },
    credit: { excellent: "Excellent (760+)", good: "Good (700–759)", fair: "Fair (660–699)", belowAvg: "Below avg (620–659)", poor: "Poor (< 620)" },
    disclaimer: "Estimates only. Rates as of Jun 2026 — confirm actual terms with a licensed lender.",
  },
  es: {
    title: "Calculadora Hipotecaria", subtitle: "Estima tu pago mensual",
    homePrice: "Precio de la casa", downPayment: "Entrada", loanType: "Opción de financiamiento",
    creditScore: "Puntaje de crédito", term: "Plazo del préstamo", rate: "Tasa de interés (APR)",
    zip: "Código postal", propertyTax: "Impuesto predial", homeInsurance: "Seguro de hogar",
    hoa: "Cuota HOA", pmi: "PMI / MIP", years: "años", perYear: "/año", perMo: "/mes",
    monthlyPayment: "Pago mensual estimado", principalInterest: "Capital e intereses",
    taxes: "Impuestos", insurance: "Seguro", hoaShort: "HOA",
    loanAmount: "Monto del préstamo", totalInterest: "Intereses totales", totalCost: "Total de pagos",
    downNote: "del precio", advanced: "Impuestos, seguro y cargos", county: "Condado",
    types: { conventional: "Convencional / Banco", fha: "FHA", va: "VA", jumbo: "Jumbo", private: "Prestamista Privado" },
    credit: { excellent: "Excelente (760+)", good: "Bueno (700–759)", fair: "Regular (660–699)", belowAvg: "Bajo prom. (620–659)", poor: "Bajo (< 620)" },
    disclaimer: "Solo estimados. Tasas a jun 2026 — confirma términos reales con un prestamista con licencia.",
  },
};

const money = (n) => "$" + Math.round(n || 0).toLocaleString("en-US");
const money2 = (n) => "$" + (n || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 });

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[0.7rem] uppercase tracking-[0.12em] font-semibold text-foreground/60">{label}</span>
        {hint && <span className="text-[0.68rem] text-primary font-medium">{hint}</span>}
      </div>
      {children}
    </label>
  );
}

const inputCls = "w-full bg-secondary/50 border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-primary transition-colors";

export function MortgageCalculator({ onClose, initialPrice = 750000, initialZip = "33139" }) {
  const { lang } = useLang();
  const L = STR[lang] || STR.en;

  const [price, setPrice] = useState(initialPrice);
  const [loanType, setLoanType] = useState("conventional");
  const [credit, setCredit] = useState("excellent");
  const [term, setTerm] = useState(30);
  const [zip, setZip] = useState(initialZip);
  const [downPct, setDownPct] = useState(LOAN_TYPES.conventional.down);
  const [rate, setRate] = useState(null); // null => auto
  const [taxPct, setTaxPct] = useState(null);
  const [insYr, setInsYr] = useState(null);
  const [hoaMo, setHoaMo] = useState(0);

  const zipInfo = useMemo(() => FL_TAX_BY_ZIP3[String(zip).slice(0, 3)] || null, [zip]);

  const autoRate = useMemo(() => {
    const base = LOAN_TYPES[loanType].rate;
    const adj = (CREDIT_TIERS.find((c) => c.id === credit) || CREDIT_TIERS[0]).adj;
    return +(base + adj).toFixed(3);
  }, [loanType, credit]);

  // When loan type changes, reset down payment default & clear manual rate.
  const onLoanType = (k) => { setLoanType(k); setDownPct(LOAN_TYPES[k].down); setRate(null); };

  const effRate = rate ?? autoRate;
  const effTaxPct = taxPct ?? (zipInfo ? zipInfo.tax : 1.1);
  const effInsYr = insYr ?? Math.round(price * 0.0055);

  const calc = useMemo(() => {
    const down = price * (downPct / 100);
    const loan = Math.max(price - down, 0);
    const n = term * 12;
    const r = effRate / 100 / 12;
    const pi = r > 0 ? (loan * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1) : loan / n;
    const taxMo = (price * (effTaxPct / 100)) / 12;
    const insMo = effInsYr / 12;
    // PMI / MIP
    let pmiMo = 0;
    if (loanType === "fha") pmiMo = (loan * 0.0055) / 12;
    else if (loanType === "conventional" && downPct < 20) pmiMo = (loan * 0.005) / 12;
    else if (loanType === "jumbo" && downPct < 20) pmiMo = (loan * 0.006) / 12;
    const totalMo = pi + taxMo + insMo + hoaMo + pmiMo;
    const totalInterest = pi * n - loan;
    return { down, loan, pi, taxMo, insMo, pmiMo, totalMo, totalInterest, totalPay: pi * n };
  }, [price, downPct, term, effRate, effTaxPct, effInsYr, hoaMo, loanType]);

  // ---- draggable + resizable ----
  const [pos, setPos] = useState(() => ({
    x: Math.max(16, (window.innerWidth || 1200) - 500),
    y: 96,
  }));
  const [size, setSize] = useState({ w: 460, h: Math.min(640, (window.innerHeight || 800) - 130) });
  const drag = useRef(null);

  const onDragStart = (e) => {
    const p = "touches" in e ? e.touches[0] : e;
    drag.current = { mode: "move", sx: p.clientX, sy: p.clientY, ox: pos.x, oy: pos.y };
  };
  const onResizeStart = (e) => {
    e.stopPropagation();
    const p = "touches" in e ? e.touches[0] : e;
    drag.current = { mode: "resize", sx: p.clientX, sy: p.clientY, ow: size.w, oh: size.h };
  };
  const onMove = useCallback((e) => {
    if (!drag.current) return;
    const p = "touches" in e ? e.touches[0] : e;
    const dx = p.clientX - drag.current.sx;
    const dy = p.clientY - drag.current.sy;
    if (drag.current.mode === "move") {
      const nx = Math.min(Math.max(0, drag.current.ox + dx), window.innerWidth - 60);
      const ny = Math.min(Math.max(0, drag.current.oy + dy), window.innerHeight - 40);
      setPos({ x: nx, y: ny });
    } else {
      setSize({
        w: Math.min(Math.max(340, drag.current.ow + dx), window.innerWidth - 20),
        h: Math.min(Math.max(320, drag.current.oh + dy), window.innerHeight - 20),
      });
    }
  }, []);
  const onUp = useCallback(() => { drag.current = null; }, []);

  useEffect(() => {
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, [onMove, onUp]);

  const rows = [
    { label: L.principalInterest, val: calc.pi, icon: Landmark, color: "text-primary" },
    { label: L.taxes, val: calc.taxMo, icon: Percent, color: "text-accent" },
    { label: L.insurance, val: calc.insMo, icon: ShieldCheck, color: "text-[#d9a441]" },
    ...(calc.pmiMo > 0 ? [{ label: L.pmi, val: calc.pmiMo, icon: TrendingDown, color: "text-foreground/70" }] : []),
    ...(hoaMo > 0 ? [{ label: L.hoaShort, val: hoaMo, icon: Building2, color: "text-foreground/70" }] : []),
  ];

  return (
    <div
      className="fixed z-[60] flex flex-col bg-background/98 backdrop-blur-xl border border-border rounded-xl shadow-[0_30px_80px_-20px_rgba(6,24,28,0.5)] overflow-hidden"
      style={{ left: pos.x, top: pos.y, width: size.w, height: size.h }}
      data-testid="mortgage-calculator"
    >
      {/* header / drag handle */}
      <div
        onMouseDown={onDragStart}
        onTouchStart={onDragStart}
        className="flex items-center gap-2 px-4 py-3 bg-gradient-to-r from-primary to-[#17808c] text-white cursor-grab active:cursor-grabbing select-none"
        data-testid="mortgage-drag-handle"
      >
        <GripVertical size={16} className="opacity-70" />
        <Calculator size={17} />
        <div className="flex-1 leading-tight">
          <div className="text-sm font-semibold tracking-wide">{L.title}</div>
          <div className="text-[0.68rem] text-white/75">{L.subtitle}</div>
        </div>
        <button onClick={onClose} className="w-8 h-8 grid place-items-center rounded-md hover:bg-white/15 transition-colors" data-testid="mortgage-close-btn" aria-label="close">
          <X size={18} />
        </button>
      </div>

      {/* body */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="mortgage-body">
        {/* loan amount */}
        <div className="flex items-center justify-between rounded-lg bg-secondary/60 border border-border px-4 py-2.5">
          <span className="text-[0.7rem] uppercase tracking-[0.12em] font-semibold text-foreground/60">{L.loanAmount}</span>
          <span className="text-lg font-semibold tabular-nums text-foreground" data-testid="mortgage-loan-amount">{money2(calc.loan)}</span>
        </div>

        {/* result summary */}
        <div className="rounded-lg border border-primary/20 bg-primary/5 p-4">
          <div className="text-[0.7rem] uppercase tracking-[0.14em] font-semibold text-foreground/55">{L.monthlyPayment}</div>
          <div className="mt-1 font-serif text-4xl font-light text-primary" data-testid="mortgage-monthly-total">{money(calc.totalMo)}<span className="text-base text-foreground/50 font-sans"> {L.perMo}</span></div>
          <div className="mt-3 space-y-1.5">
            {rows.map((row) => (
              <div key={row.label} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2 text-foreground/70"><row.icon size={14} className={row.color} /> {row.label}</span>
                <span className="font-medium tabular-nums">{money(row.val)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* finance option */}
        <Field label={L.loanType}>
          <div className="grid grid-cols-3 gap-1.5">
            {Object.keys(LOAN_TYPES).map((k) => (
              <button key={k} onClick={() => onLoanType(k)}
                className={`px-2 py-2 rounded-md text-xs font-medium border transition-all duration-200 ${loanType === k ? "bg-primary text-white border-primary shadow-sm" : "bg-secondary/40 border-border text-foreground/70 hover:border-primary"}`}
                data-testid={`mortgage-loantype-${k}`}>
                {L.types[k]}
              </button>
            ))}
          </div>
        </Field>

        {/* price + down */}
        <div className="grid grid-cols-2 gap-3">
          <Field label={L.homePrice}>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground/50">$</span>
              <input type="number" min="0" value={price} onChange={(e) => setPrice(+e.target.value || 0)} className={inputCls + " pl-6"} data-testid="mortgage-price" />
            </div>
          </Field>
          <Field label={L.downPayment} hint={`${money(price * downPct / 100)}`}>
            <div className="relative">
              <input type="number" min="0" max="100" step="0.5" value={downPct} onChange={(e) => setDownPct(+e.target.value || 0)} className={inputCls + " pr-7"} data-testid="mortgage-down" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-foreground/50">%</span>
            </div>
          </Field>
        </div>

        {/* credit + term */}
        <div className="grid grid-cols-2 gap-3">
          <Field label={L.creditScore}>
            <select value={credit} onChange={(e) => setCredit(e.target.value)} className={inputCls} data-testid="mortgage-credit">
              {CREDIT_TIERS.map((c) => <option key={c.id} value={c.id}>{L.credit[c.id]}</option>)}
            </select>
          </Field>
          <Field label={L.term}>
            <select value={term} onChange={(e) => setTerm(+e.target.value)} className={inputCls} data-testid="mortgage-term">
              {[30, 20, 15, 10].map((y) => <option key={y} value={y}>{y} {L.years}</option>)}
            </select>
          </Field>
        </div>

        {/* rate + zip */}
        <div className="grid grid-cols-2 gap-3">
          <Field label={L.rate} hint={rate === null ? "auto" : "edited"}>
            <div className="relative">
              <input type="number" min="0" step="0.05" value={effRate} onChange={(e) => setRate(+e.target.value || 0)} className={inputCls + " pr-7"} data-testid="mortgage-rate" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-foreground/50">%</span>
            </div>
          </Field>
          <Field label={L.zip} hint={zipInfo ? zipInfo.county : undefined}>
            <input value={zip} onChange={(e) => { setZip(e.target.value); setTaxPct(null); }} maxLength={5} className={inputCls} data-testid="mortgage-zip" />
          </Field>
        </div>

        {/* advanced */}
        <div className="pt-1">
          <div className="text-[0.7rem] uppercase tracking-[0.14em] font-semibold text-foreground/45 mb-2 flex items-center gap-2"><Home size={13} /> {L.advanced}</div>
          <div className="grid grid-cols-3 gap-3">
            <Field label={L.propertyTax}>
              <div className="relative">
                <input type="number" step="0.01" value={effTaxPct} onChange={(e) => setTaxPct(+e.target.value || 0)} className={inputCls + " pr-6 text-sm"} data-testid="mortgage-tax" />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-foreground/50">%</span>
              </div>
            </Field>
            <Field label={L.homeInsurance + " " + L.perYear}>
              <div className="relative">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-foreground/50">$</span>
                <input type="number" value={effInsYr} onChange={(e) => setInsYr(+e.target.value || 0)} className={inputCls + " pl-5 text-sm"} data-testid="mortgage-ins" />
              </div>
            </Field>
            <Field label={L.hoa + " " + L.perMo}>
              <div className="relative">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-foreground/50">$</span>
                <input type="number" value={hoaMo} onChange={(e) => setHoaMo(+e.target.value || 0)} className={inputCls + " pl-5 text-sm"} data-testid="mortgage-hoa" />
              </div>
            </Field>
          </div>
        </div>

        <p className="text-[0.66rem] text-foreground/45 leading-relaxed pt-1">{L.disclaimer}</p>
      </div>

      {/* resize handle */}
      <div
        onMouseDown={onResizeStart}
        onTouchStart={onResizeStart}
        className="absolute bottom-0 right-0 w-5 h-5 cursor-se-resize"
        data-testid="mortgage-resize-handle"
        style={{ background: "linear-gradient(135deg, transparent 50%, hsl(var(--brand-green) / 0.55) 50%)" }}
      />
    </div>
  );
}
